import {
  type APIConfiguration,
  Service,
  SWRConfiguration,
  useList,
  useListWithCount,
  useRead,
} from "@api/common";
import type { Flow, FlowAvailableField, FlowChange, ListParams, ReadParams } from "./types";
import { getParamsWithPagination } from "./utils";

export type { Flow };
export class FlowService extends Service<Flow> {
  constructor() {
    super("/flows");
  }

  async change(id: string, data: FlowChange): Promise<Flow> {
    return this.post(`${this.basePath}/${id}/changes`, data);
  }

  /**
   * Sample the fields available for dynamic insertion for a `customer_event`
   * trigger — both event properties (prefixed `event.`) and contact properties
   * (prefixed `customer.`). Mirrors the customer-event `available-properties`
   * endpoint.
   */
  async listAvailableFields(params: { gym: string; type: string; event: string; sample: number }) {
    return this.get<{ fields: Record<string, FlowAvailableField> }>(
      `${this.basePath}/available-fields`,
      params,
    );
  }

  /**
   * Sample example values for a single dynamic field path. `sample` is only
   * used for `event.*` fields; `query` narrows `customer.*` field values.
   */
  async listAvailableFieldValues(params: {
    gym: string;
    type: string;
    event: string;
    field: string;
    sample?: number;
    query?: string;
  }) {
    return this.get<{ values: string[] }>(`${this.basePath}/available-field-values`, params);
  }
}

export const flowService = new FlowService();

export function useFlows(params?: ListParams<Flow>, config?: APIConfiguration) {
  return useList(flowService, params, config);
}

export function useFlowsWithCount(params?: ListParams<Flow>, config?: SWRConfiguration) {
  return useListWithCount(flowService, { ...getParamsWithPagination(params) }, config);
}

export function useFlow(id?: string, params?: ReadParams, config?: SWRConfiguration) {
  return useRead(flowService, id, params, config);
}
